import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import heroBanner from '@/assets/home-hero-upload.png';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { Megaphone, Bell, ChevronLeft, Heart, Shield, Star, Rocket } from 'lucide-react';

// 3D Icons
import iconInnovation from '@/assets/icons/icon-innovation.png';
import iconIdeas from '@/assets/icons/icon-ideas.png';
import iconAchievements from '@/assets/icons/icon-achievements.png';
import iconCommunity from '@/assets/icons/icon-community.png';
import iconLearning from '@/assets/icons/icon-learning.png';
import iconCertificates from '@/assets/icons/icon-certificates.png';
import iconExperts from '@/assets/icons/icon-experts.png';
import iconSmart from '@/assets/icons/icon-smart.png';
import iconEvents from '@/assets/icons/icon-events.png';
import iconPartnerships from '@/assets/icons/icon-partnerships.png';
import iconMental from '@/assets/icons/icon-mental.png';
import iconAdmin from '@/assets/icons/icon-admin.png';
import iconGift from '@/assets/icons/icon-gift.png';
import iconKifahValues from '@/assets/kifah-values.jpeg';

/* ── Card data ── */
const cardData = [
  { num: 1, img: iconInnovation, arTitle: 'حاضنة الابتكار', enTitle: 'Innovation Hub', arDesc: 'مساحة لاحتضان الأفكار وتطويرها\nوتحويلها إلى مبادرات رائدة', enDesc: 'A space to nurture ideas and turn them into initiatives', url: '/innovation', badge: 'bg-red-500', ctaStyle: 'bg-primary text-white' },
  { num: 2, img: iconIdeas, arTitle: 'صندوق الأفكار', enTitle: 'Idea Box', arDesc: 'أطلق فكرتك وكن جزءً من التغيير\nوابنِ مستقبلاً أفضل', enDesc: 'Launch your idea and be part of the change', url: '/ideas', badge: 'bg-green-500', ctaStyle: 'border border-primary/30 text-primary' },
  { num: 3, img: iconAchievements, arTitle: 'الإنجازات', enTitle: 'Achievements', arDesc: 'استعرض إنجازاتك وتتبع تقدمك\nفي رحلة الابتكار', enDesc: 'Track your progress in the innovation journey', url: '/achievements', badge: 'bg-orange-500', ctaStyle: 'border border-primary/30 text-primary' },
  { num: 4, img: iconCommunity, arTitle: 'المجتمع', enTitle: 'Community', arDesc: 'تواصل وتعاون مع المبدعين\nوالمبتكرين', enDesc: 'Connect and collaborate with innovators', url: '/skills', badge: 'bg-emerald-500', ctaStyle: 'border border-primary/30 text-primary' },
  { num: 5, img: iconLearning, arTitle: 'المصادر التعليمية', enTitle: 'Learning Resources', arDesc: 'اكتشف مقالات ودروس وموارد\nلتطوير مهاراتك', enDesc: 'Discover articles and resources to develop skills', url: '/experts', badge: 'bg-teal-500', ctaStyle: 'border border-primary/30 text-primary' },
  { num: 6, img: iconCertificates, arTitle: 'الشهادات', enTitle: 'Certificates', arDesc: 'احصل على شهادات معتمدة\nلإنجازاتك وجهودك', enDesc: 'Get certified for your achievements and efforts', url: '/challenge', badge: 'bg-purple-500', ctaStyle: 'border border-primary/30 text-primary' },
];

const extraCards = [
  { num: 7, img: iconExperts, arTitle: 'تبادل الخبرات', enTitle: 'Expert Mentors', arDesc: 'تبادل الخبرات بين المعلمات\nوالمختصين', enDesc: 'Exchange expertise among teachers', url: '/experts', badge: 'bg-indigo-500', ctaStyle: 'border border-primary/30 text-primary' },
  { num: 8, img: iconSmart, arTitle: 'الكفاح الذكي', enTitle: 'Smart Kifah', arDesc: 'حلول ذكية وتقنيات تدعم الابتكار', enDesc: 'Smart solutions for innovation', url: '/smart', badge: 'bg-cyan-500', ctaStyle: 'border border-primary/30 text-primary' },
  { num: 9, img: iconEvents, arTitle: 'تقييم الفعاليات', enTitle: 'Events', arDesc: 'شاركنا رأيك وساعدنا في التطوير', enDesc: 'Share your feedback', url: '/events', badge: 'bg-rose-500', ctaStyle: 'border border-primary/30 text-primary' },
  { num: 10, img: iconPartnerships, arTitle: 'الشراكة المجتمعية', enTitle: 'Partnerships', arDesc: 'نبني شراكات تصنع الأثر', enDesc: 'Building impactful partnerships', url: '/partnerships', badge: 'bg-pink-500', ctaStyle: 'border border-primary/30 text-primary' },
  { num: 11, img: iconMental, arTitle: 'الدعم النفسي', enTitle: 'Mental Health', arDesc: 'بيئة داعمة تعزز الصحة النفسية', enDesc: 'A supportive environment for well-being', url: '/mental-health', badge: 'bg-red-400', ctaStyle: 'border border-primary/30 text-primary' },
  { num: 12, img: iconAdmin, arTitle: 'لوحة الإدارة', enTitle: 'Admin', arDesc: 'إدارة المنصة ومتابعة الأداء', enDesc: 'Manage platform and performance', url: '/admin', badge: 'bg-slate-500', ctaStyle: 'border border-primary/30 text-primary' },
  { num: 13, img: iconGift, arTitle: 'إهداء', enTitle: 'Dedications', arDesc: 'شكر وتقدير لمن يصنع الأثر\nويدعم مسيرة الابتكار', enDesc: 'Appreciation for those who make an impact', url: '/dedications', badge: 'bg-amber-500', ctaStyle: 'border border-amber-400 text-amber-600' },
  { num: 14, img: iconKifahValues, arTitle: 'قيم الكفاح', enTitle: 'Alkifah Values', arDesc: 'رحلة ملهمة في قيم الكفاح\nالتي تصنع جيلاً مبدعاً', enDesc: 'An inspiring journey through Alkifah values', url: '/kifah-values', badge: 'bg-emerald-600', ctaStyle: 'border border-emerald-500 text-emerald-700', featured: true as const },
];

const allCardsBase = [...cardData, ...extraCards];

const featuresData = [
  { icon: Heart, arTitle: 'مجتمع ملهم', enTitle: 'Inspiring Community', arDesc: 'انضم إلى مجتمع من المبدعين والقادة', enDesc: 'Join a community of creators', color: 'text-pink-500' },
  { icon: Star, arTitle: 'جودة وتميز', enTitle: 'Quality & Excellence', arDesc: 'نلتزم بتقديم أفضل تجربة تعليمية وابتكارية', enDesc: 'Best educational experience', color: 'text-amber-500' },
  { icon: Rocket, arTitle: 'ابتكار بلا حدود', enTitle: 'Limitless Innovation', arDesc: 'اكتشف قدراتك واطلق إبداعاتك', enDesc: 'Unleash your creativity', color: 'text-red-500' },
  { icon: Shield, arTitle: 'آمن وموثوق', enTitle: 'Safe & Trusted', arDesc: 'حماية بياناتك وخصوصيتك بأعلى المعايير', enDesc: 'Data protected to highest standards', color: 'text-emerald-500' },
];

/* ── Animations ── */
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } } };
const fadeUp = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring' as const, stiffness: 120, damping: 16 } },
};

/* ── Component ── */
const DashboardGrid = () => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState<string[]>([]);
  const [bannerLoaded, setBannerLoaded] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const { scrollY } = useScroll();
  const bannerY = useTransform(scrollY, [0, 500], [0, 60]);
  const isRTL = lang === 'ar';

  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase.from('admin_roles').select('id').eq('user_id', user.id).maybeSingle();
        setIsAdmin(!!data);
      }
    };
    checkAdmin();
  }, []);

  useEffect(() => {
    const fetch_ = async () => {
      const { data, error } = await supabase.from('announcements').select('*').eq('is_active', true).order('sort_order', { ascending: true });
      if (error) { setAnnouncements([]); setAnnouncementsLoading(false); return; }

      const ordered = [...(data || [])].sort((a, b) => {
        const aPriority = Number(
          a.text_ar.includes('Codeavour') || a.text_ar.includes('المركز الثالث') || a.text_ar.includes('العالمية')
        );
        const bPriority = Number(
          b.text_ar.includes('Codeavour') || b.text_ar.includes('المركز الثالث') || b.text_ar.includes('العالمية')
        );

        return bPriority - aPriority || a.sort_order - b.sort_order || new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      setAnnouncements(ordered.map((a) => `${a.emoji} ${t(a.text_ar, a.text_en)}`));
      setAnnouncementsLoading(false);
    };
    fetch_();
    const ch = supabase.channel('ann-rt').on('postgres_changes', { event: '*', schema: 'public', table: 'announcements' }, () => fetch_()).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [t]);

  const tickerItems = (announcements.length > 0 ? announcements : [t('آخر الإعلانات ستظهر هنا قريبًا', 'Latest announcements will appear here soon')]).sort((a, b) => Number(b.includes('Codeavour') || b.includes('العالمية') || b.includes('المركز الثالث')) - Number(a.includes('Codeavour') || a.includes('العالمية') || a.includes('المركز الثالث')));

  const allCards = isAdmin ? allCardsBase : allCardsBase.filter(c => c.url !== '/admin');

  return (
    <div className="bg-background">
      {/* ═══ HERO ═══ */}
      <div className="relative w-full overflow-hidden rounded-b-3xl">
        {!bannerLoaded && <Skeleton className="w-full aspect-auto" />}
        <motion.img
          src={heroBanner}
          alt={t('أكاديمية الكفاح للابتكار', 'Al-Kifah Academy for Innovation')}
          className={`w-full h-auto block will-change-transform ${!bannerLoaded ? 'invisible absolute' : ''}`}
          style={{ y: bannerY }}
          onLoad={() => setBannerLoaded(true)}
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 via-[30%] to-transparent" />
      </div>

      {/* ═══ ANNOUNCEMENT BAR ═══ */}
      <div className="mx-4 md:mx-8 max-w-5xl lg:mx-auto -mt-5 relative z-10">
        <div className="flex items-center gap-3 bg-white rounded-full border border-border/50 shadow-md px-3 sm:px-4 py-2.5 overflow-hidden">
          <div className="w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Megaphone className="w-5 h-5 text-white" />
          </div>

          <div className="flex-1 overflow-hidden min-w-0">
            {tickerItems.length > 0 && (
              <div dir="ltr" className="animate-marquee flex min-w-max items-center gap-6 whitespace-nowrap will-change-transform">
                {[...tickerItems, ...tickerItems].map((text, i) => (
                  <span key={i} dir={isRTL ? 'rtl' : 'ltr'} className="text-sm font-semibold text-foreground pe-6">
                    {text}
                  </span>
                ))}
              </div>
            )}
          </div>

          <button className="shrink-0 flex items-center gap-1.5 px-3 sm:px-4 py-1.5 rounded-full border-2 border-primary text-primary text-[11px] sm:text-xs font-bold hover:bg-primary hover:text-white transition-all">
            <Bell className="w-3.5 h-3.5" />
            {t('تابعنا للمزيد', 'Follow Us')}
          </button>
        </div>
      </div>

      {/* ═══ HERO TITLE ═══ */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center pt-10 pb-2 px-4"
      >
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-foreground font-heading leading-tight mb-3">
          {t('أكاديمية الكفاح للابتكار', 'Al-Kifah Innovation Academy')}
        </h1>
        <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          {t('منصة رقمية تفاعلية لتمكين الطالبات والمعلمات وأولياء الأمور من الإبداع والابتكار', 'An interactive digital platform empowering students, teachers, and parents to create and innovate')}
        </p>
      </motion.div>

      {/* ═══ SECTION TITLE ═══ */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center pt-8 pb-4 px-4"
      >
        <h2 className="text-xl sm:text-2xl font-black text-foreground font-heading">
          ✨ {t('استكشف المنصة', 'Explore the Platform')} ✨
        </h2>
      </motion.div>

      {/* ═══ CARDS GRID ═══ */}
      <div className="px-4 md:px-8 pb-6">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
          className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 max-w-5xl mx-auto"
        >
          {allCards.map((card) => {
            const isFeatured = (card as any).featured;
            if (isFeatured) {
              return (
                <motion.button
                  key={card.num}
                  variants={fadeUp}
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate(card.url)}
                  className="relative col-span-2 sm:col-span-2 md:col-span-3 overflow-hidden rounded-3xl group cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-500 min-h-[220px] sm:min-h-[260px] border-2 border-emerald-500/40"
                >
                  {/* Background image */}
                  <img
                    src={card.img}
                    alt={t(card.arTitle, card.enTitle)}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/85 via-emerald-800/40 to-emerald-700/10" />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-transparent" />

                  {/* Glow */}
                  <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-yellow-300/30 blur-3xl group-hover:bg-yellow-300/50 transition-all duration-700" />

                  {/* Badge */}
                  <span className="absolute top-3 end-3 px-3 py-1 rounded-full bg-yellow-400 text-emerald-900 text-[10px] font-black shadow-lg backdrop-blur">
                    ✨ {t('جديد', 'NEW')}
                  </span>

                  {/* Content */}
                  <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 text-start">
                    <h3 className="text-2xl sm:text-3xl font-black text-white font-heading mb-1.5 drop-shadow-lg">
                      🌿 {t(card.arTitle, card.enTitle)}
                    </h3>
                    <p className="text-xs sm:text-sm text-emerald-50/90 leading-relaxed mb-3 max-w-md drop-shadow">
                      {t(card.arDesc, card.enDesc)}
                    </p>
                    <span className="inline-flex items-center gap-1.5 bg-white text-emerald-700 font-black text-xs sm:text-sm rounded-full px-5 py-2 shadow-lg group-hover:bg-emerald-500 group-hover:text-white transition-all duration-300">
                      {t('اكتشف القيم', 'Discover Values')}
                      <ChevronLeft className="w-3.5 h-3.5 rtl:rotate-0 ltr:rotate-180" />
                    </span>
                  </div>
                </motion.button>
              );
            }
            return (
              <motion.button
                key={card.num}
                variants={fadeUp}
                whileHover={{ y: -6, boxShadow: '0 12px 32px -8px rgba(124,58,237,0.18)' }}
                onClick={() => navigate(card.url)}
                className="relative flex flex-col items-center text-center bg-white rounded-2xl p-5 group cursor-pointer transition-all duration-300 border border-border/50 shadow-sm hover:shadow-lg hover:border-primary/20"
              >
                {/* Badge */}
                <span className={`absolute top-2.5 end-2.5 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow ${card.badge}`}>
                  {card.num}
                </span>

                {/* 3D Icon */}
                <div className="w-16 h-16 flex items-center justify-center transition-transform duration-300 group-hover:scale-110 mb-3">
                  <img src={card.img} alt={card.enTitle} className="w-14 h-14 object-contain" loading="lazy" width={56} height={56} />
                </div>

                {/* Title */}
                <h3 className="text-sm font-bold text-foreground font-heading mb-1">{t(card.arTitle, card.enTitle)}</h3>

                {/* Description - single line */}
                <p className="text-[11px] text-muted-foreground leading-relaxed mb-4 line-clamp-2">{t(card.arDesc, card.enDesc)}</p>

                {/* CTA Button */}
                <span className={`mt-auto inline-flex items-center gap-0.5 text-[11px] font-bold rounded-full px-4 py-1.5 transition-all duration-300 ${card.ctaStyle} group-hover:bg-primary group-hover:text-white`}>
                  {t('ابدأ الآن', 'Start Now')}
                  <ChevronLeft className="w-3 h-3 rtl:rotate-0 ltr:rotate-180" />
                </span>
              </motion.button>
            );
          })}
        </motion.div>
      </div>

      {/* ═══ FEATURES BAR ═══ */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="px-4 md:px-8 pb-8 max-w-5xl lg:mx-auto"
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-white rounded-2xl border border-border/40 p-5 shadow-sm">
          {featuresData.map((f, i) => {
            const FIcon = f.icon;
            return (
              <div key={i} className="flex flex-col items-center text-center gap-1.5 py-2">
                <FIcon className={`w-8 h-8 ${f.color}`} />
                <h4 className="text-xs font-bold text-foreground font-heading">{t(f.arTitle, f.enTitle)}</h4>
                <p className="text-[10px] text-muted-foreground leading-snug">{t(f.arDesc, f.enDesc)}</p>
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
};

export default DashboardGrid;
